import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Auth from './pages/Auth';
import Home from './pages/Home';
import DocumentDetail from './pages/DocumentDetail';
import Profile from './pages/Profile';
import Upload from './pages/Upload';
import Messages from './pages/Messages'; // disabled
import Notifications from './pages/Notifications';
import Purchases from './pages/Purchases';
import PaymentSuccess from './pages/PaymentSuccess';
import Earnings from './pages/Earnings';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ResetPassword from './pages/ResetPassword';
import Admin from './pages/Admin';
import StudyDashboard from './pages/StudyDashboard';
import StudyBuilder from './pages/StudyBuilder';
import BrowseCourses from './pages/BrowseCourses';
import CourseHub from './pages/CourseHub';
import BulkUpload from './pages/BulkUpload';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, tokenData) => {
    setUser(userData);
    setToken(tokenData);
    localStorage.setItem('token', tokenData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, token, login, logout }}>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/auth" element={!user ? <Auth /> : <Navigate to="/" />} />
            <Route path="/" element={user ? <Home /> : <Navigate to="/auth" />} />
            <Route path="/document/:id" element={user ? <DocumentDetail /> : <Navigate to="/auth" />} />
            <Route path="/profile/:id" element={user ? <Profile /> : <Navigate to="/auth" />} />
            <Route path="/upload" element={user ? <Upload /> : <Navigate to="/auth" />} />
            {/* Messages route disabled */}
            <Route path="/notifications" element={user ? <Notifications /> : <Navigate to="/auth" />} />
            <Route path="/purchases" element={user ? <Purchases /> : <Navigate to="/auth" />} />
            <Route path="/earnings" element={user ? <Earnings /> : <Navigate to="/auth" />} />
            <Route path="/payment-success" element={user ? <PaymentSuccess /> : <Navigate to="/auth" />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin" element={user ? <Admin /> : <Navigate to="/auth" />} />
            <Route path="/study/:documentId" element={user ? <StudyDashboard /> : <Navigate to="/auth" />} />
            <Route path="/study-builder" element={user ? <StudyBuilder /> : <Navigate to="/auth" />} />
            <Route path="/courses" element={user ? <BrowseCourses /> : <Navigate to="/auth" />} />
            <Route path="/course/:slug" element={user ? <CourseHub /> : <Navigate to="/auth" />} />
            <Route path="/bulk-upload" element={user ? <BulkUpload user={user} /> : <Navigate to="/auth" />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
